/**
 * CampaignFailureReason — why a campaign reached `failed`, in the one closed
 * vocabulary the console renders and every refusal on the build path resolves
 * to. A member exists only where it names a DIFFERENT thing for the operator
 * to go and do; two conditions sharing one member send one of them somewhere
 * that cannot fix it.
 *
 * The build's own refusals and the ad client's classified Graph failures both
 * land here rather than in two vocabularies. The operator asks one question —
 * why did the money I committed not get spent — and has no interest in which
 * layer of ours answered it. The ad client's routing vocabulary (`MetaFailure`
 * in the functions workspace: the nine members a Graph call can throw) is
 * `.extract`ed from this set rather than re-listed beside it, so the token a
 * Graph error classifies to and the token the campaign document stores are the
 * same string by construction.
 *
 * Members justified — grouped by where the remedy is:
 *
 * Finished here, on this product's own screens:
 *   notConfigured     — a precondition of the feature is absent, so the ad was
 *                       never composed or never posted: no legal identity to
 *                       name as the DSA payor, no ad account, Page or pixel,
 *                       no spend ceiling, no stored credential, or an ad
 *                       account with no funding source.
 *   budgetOutOfBounds — the amount is below the minimum Meta publishes for the
 *                       account's currency, or above the workspace's own
 *                       ceiling.
 *   worldMoved        — the price the composed copy quotes is no longer the
 *                       price, so publishing those words would advertise a
 *                       figure this event does not sell at.
 *   creativeUnusable  — the chosen picture is not one that can be sent: bytes
 *                       no reader here can measure, geometry disagreeing with
 *                       the document describing it, or an upload Meta itself
 *                       refused. The remedy is the same wherever the refusal
 *                       came from, which is why one member carries all three.
 *
 * Fixed at Meta:
 *   deadCredential    — the token itself is rejected. Re-connect the account.
 *   notPermitted      — a real token Meta refuses for this asset. Re-assign
 *                       the ad account or the Page to it in Business Manager.
 *   adReview          — the activation refused for a reason no error code
 *                       claims, which is Meta having reviewed the AD. Change
 *                       the words or the picture.
 *   advertisingBlocked— Meta has barred this advertiser from acting at all,
 *                       temporarily or for good. It is the one member whose
 *                       remedy is wholly outside this product: no campaign,
 *                       picture or amount is the reason, so the operator is
 *                       told that plainly rather than sent to change one, and
 *                       is never invited to retry against a standing block.
 *                       Distinct from `notPermitted`, whose remedy is an asset
 *                       re-assignment that cannot lift a block, and from
 *                       `adReview`, which judges ONE ad rather than the actor.
 *
 * Neither:
 *   throttled         — the ad account's hourly budget is spent. Nothing is
 *                       wrong with the campaign, and launching it again later
 *                       is the whole remedy.
 *   unclassified      — nothing above claims it. Says nothing about the
 *                       credential either way, which is why it exists: without
 *                       it the last bucket standing becomes the home for every
 *                       transient 500, and one of the three above would start
 *                       sending operators to re-mint a working token on Meta's
 *                       bad days. It is also what keeps absence meaning one
 *                       thing — a failed campaign carrying no reason failed
 *                       before this field existed, which is a different fact
 *                       from one nobody could classify.
 *
 * Excluded:
 *   a free-text detail beside the member — the strings behind these
 *     classifications are our own English, a truncated Graph body, or a
 *     ZodError message, so rendering one is either an untranslated string in a
 *     product whose every other word is translated, or a raw Graph payload on
 *     a document the console reads. `Campaign.reviewFeedback` earns its
 *     verbatim rendering by being a QUOTATION, presented as Meta's own words;
 *     ours would be neither attributed nor translated. The detail stays in the
 *     structured log, joined on the campaign id. A figure a member genuinely
 *     needs — Meta's published minimum behind `budgetOutOfBounds`, the lockout
 *     behind `throttled` — lands as its own typed field with its own named
 *     reader, never as prose.
 *   a member per Graph error code — the code decides which member, and the
 *     member decides what the operator does; a vocabulary keyed on the codes
 *     themselves would be Meta's to publish and would grow with theirs.
 */
import { z } from "zod";

export const CampaignFailureReasonSchema = z.enum([
	"notConfigured",
	"budgetOutOfBounds",
	"worldMoved",
	"creativeUnusable",
	"deadCredential",
	"notPermitted",
	"adReview",
	"advertisingBlocked",
	"throttled",
	"unclassified",
]);
export type CampaignFailureReason = z.infer<typeof CampaignFailureReasonSchema>;

/**
 * Whether the failed face offers to compose the campaign again.
 *
 * The press recomposes at the amount the campaign already carries and parks a
 * fresh proposal — the "build a new proposal" and the "then try again" these
 * members' own sentences name. It is offered wherever some operator action can
 * end in a composition that runs, including one taken first somewhere this
 * product does not own: re-connecting the account, granting the ad account or
 * the Page in Business Manager, an admin raising a ceiling.
 *
 * It is withheld only where no such action exists, which is `advertisingBlocked`
 * alone. Nothing about the campaign, the picture or the amount is the reason
 * there, so the press would compose, spend the model call, park a proposal, and
 * hand the same block back one confirmation later — progress that is not
 * progress. That member's own entry above puts the remedy wholly outside this
 * product, and no control here could honestly stand beside it.
 *
 * Keyed by the member, so a reason cannot join the vocabulary without answering
 * this.
 */
export const RETRY_OFFERED = {
	notConfigured: true,
	budgetOutOfBounds: true,
	worldMoved: true,
	creativeUnusable: true,
	deadCredential: true,
	notPermitted: true,
	adReview: true,
	advertisingBlocked: false,
	throttled: true,
	unclassified: true,
} as const satisfies Record<CampaignFailureReason, boolean>;
